import { useEffect } from 'react';

/**
 * Reveals every [data-reveal] element in the document as it scrolls into
 * view by adding `.is-revealed` (see the rules in App.css).
 *
 * Mounted once at the app root and re-run on route changes, so pages don't
 * each need their own observer. Elements are unobserved after revealing —
 * this is a one-way transition, not a scroll-linked effect.
 *
 * Content is never hidden by this: the CSS only hides [data-reveal] under
 * `prefers-reduced-motion: no-preference`, and if IntersectionObserver is
 * missing everything is revealed immediately.
 */
export default function useScrollReveal(deps = []) {
  useEffect(() => {
    const targets = Array.from(document.querySelectorAll('[data-reveal]:not(.is-revealed)'));
    if (targets.length === 0) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return undefined;
    }

    let pending = new Set(targets);

    const reveal = (el) => {
      el.classList.add('is-revealed');
      pending.delete(el);
      observer.unobserve(el);
      if (pending.size === 0) teardown();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) reveal(entry.target);
        });
      },
      // Start slightly before the element reaches the fold so the motion has
      // finished by the time it is properly in view.
      { rootMargin: '0px 0px -10% 0px', threshold: 0 }
    );

    targets.forEach((el) => observer.observe(el));

    /* Backstop. The observer only samples at frame boundaries, so a fast
       flick, a jump to an anchor, or a programmatic scroll can carry an
       element past the viewport without ever firing a callback — which
       would leave that content permanently invisible. This sweep reveals
       anything at or above the fold regardless. */
    let ticking = false;
    const sweep = () => {
      ticking = false;
      Array.from(pending).forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight) reveal(el);
      });
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(sweep);
    };

    function teardown() {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    const raf = window.requestAnimationFrame(sweep);

    return () => {
      window.cancelAnimationFrame(raf);
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
